import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { LOCAL_EDIT_ECHO_WINDOW_MS } from '../contexts/collaborativeEncounter.context';

/**
 * Field-specific steps, run at fixed moments. Each list runs in order.
 * Read through a ref, so inline arrays/closures don't re-run the incoming
 * sync and always see the caller's latest render.
 */
export type EchoGuardedNoteHandlers<T> = {
  /** Persists the note (debounced Yjs write, or plain state outside an encounter) */
  write?: (value: T) => void;
  /** Keystroke: after the local value updates, before write */
  onLocalEdit?: Array<(value: T) => void>;
  /** Incoming: any false skips the value outright — not deferred, never applied */
  shouldApply?: Array<(incoming: T) => boolean>;
  /** Incoming: after the local value takes it. `previous` is the local value it replaced. */
  onApply?: Array<(incoming: T, previous: T) => void>;
};

/**
 * The single local-note pattern for collaborative note fields: local state,
 * a guard against stale Yjs echoes, and the field's write path.
 *
 * A stale echo would revert keystrokes and jump the cursor, so incoming
 * values are deferred while a local edit is recent and re-applied by a
 * deferred recheck once typing goes idle. A complaintId switch always loads
 * the new note, ignoring the window.
 *
 * Route keystrokes through editLocalNote(). setLocalNote() changes only the
 * displayed value (no edit stamp, no write).
 */
export const useEchoGuardedLocalNote = <T = string>({
  incoming,
  complaintId,
  flushPendingWrites,
  ...handlers
}: {
  incoming: T;
  complaintId?: string;
  flushPendingWrites?: () => void;
} & EchoGuardedNoteHandlers<T>): {
  localNote: T;
  setLocalNote: (value: T) => void;
  editLocalNote: (value: T) => void;
  markLocalEdit: () => void;
} => {
  const [localNote, setLocalNoteState] = useState(incoming);
  // Mirrors localNote for onApply's `previous` — state is stale inside timers
  const localNoteRef = useRef(incoming);
  const lastLocalEditAtRef = useRef(0);
  const lastComplaintIdRef = useRef(complaintId);
  const latestRef = useRef({ flushPendingWrites, handlers });

  useLayoutEffect(() => {
    latestRef.current = { flushPendingWrites, handlers };
  });

  const setLocalNote = useCallback((value: T) => {
    localNoteRef.current = value;
    setLocalNoteState(value);
  }, []);

  useEffect(() => {
    const { flushPendingWrites: flush, handlers: current } = latestRef.current;

    const apply = () => {
      const previous = localNoteRef.current;
      setLocalNote(incoming);
      current.onApply?.forEach(fn => fn(incoming, previous));
    };

    const complaintChanged = lastComplaintIdRef.current !== complaintId;
    lastComplaintIdRef.current = complaintId;

    if (complaintChanged) {
      // Land any pending edit for the previous complaint. Click navigation
      // already flushes via the textarea's onBlur, but programmatic or
      // back/forward navigation doesn't blur first.
      flush?.();
      lastLocalEditAtRef.current = 0;
      apply();

      return undefined;
    }

    if (current.shouldApply?.some(fn => !fn(incoming))) {
      return undefined;
    }

    const elapsed = Date.now() - lastLocalEditAtRef.current;

    if (elapsed < LOCAL_EDIT_ECHO_WINDOW_MS) {
      let timer: ReturnType<typeof setTimeout>;

      const applyWhenIdle = () => {
        const idleFor = Date.now() - lastLocalEditAtRef.current;

        if (idleFor >= LOCAL_EDIT_ECHO_WINDOW_MS) {
          apply();
        } else {
          timer = setTimeout(applyWhenIdle, LOCAL_EDIT_ECHO_WINDOW_MS - idleFor + 50);
        }
      };

      timer = setTimeout(applyWhenIdle, LOCAL_EDIT_ECHO_WINDOW_MS - elapsed + 50);

      return () => clearTimeout(timer);
    }

    apply();

    return undefined;
  }, [incoming, complaintId, setLocalNote]);

  const markLocalEdit = useCallback(() => {
    lastLocalEditAtRef.current = Date.now();
  }, []);

  const editLocalNote = useCallback(
    (value: T) => {
      const { handlers: current } = latestRef.current;

      markLocalEdit();
      setLocalNote(value);
      current.onLocalEdit?.forEach(fn => fn(value));
      current.write?.(value);
    },
    [markLocalEdit, setLocalNote]
  );

  return { localNote, setLocalNote, editLocalNote, markLocalEdit };
};
